import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { Link, useLocation } from "react-router-dom";
import { SessionContext } from "../context/sessionContext";
import { CircularProgress } from "@mui/material";

const ENVIRONMENT = process.env.REACT_APP_ENVIRONMENT;
const PROJECT_COMPLETION_DURATION_MS =
  ENVIRONMENT === "production" ? 36 * 60 * 60 * 1000 : 2 * 60 * 60 * 1000;

const GeneratingInsights = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getUserId } = useContext(SessionContext);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const [deliveryTime, setDeliveryTime] = useState("");
  const [projectData, setProjectData] = useState(null);
  const [isComplete, setIsComplete] = useState(false);
  const [isLoadingProject, setIsLoadingProject] = useState(true);
  const steps = [
    {
      id: 1,
      title: "Sampling\nRespondents",
      description:
        "We select the right mix of people to ensure balanced, reliable feedback.",
      percentage: 10,
    },
    {
      id: 2,
      title: "Generating\nInsights",
      description:
        "Key patterns and takeaways emerge as we process the data in real time.",
      percentage: 20,
    },
    {
      id: 3,
      title: "Analyzing\nResponses",
      description:
        "We dig deeper into the details to uncover meaning behind the answers.",
      percentage: 30,
    },
    {
      id: 4,
      title: "Identifying Strategic Segments",
      description:
        "We group audiences by shared traits to highlight opportunities.",
      percentage: 20,
    },
    {
      id: 5,
      title: "Creating\nDeliverables",
      description:
        "Findings are packaged into clear, actionable outputs you can use right away.",
      percentage: 20,
    },
  ];

  useEffect(() => {
    const initializeProject = async () => {
      if (location.state?.projectId && location.state?.createdAt) {
        const newProjectData = {
          projectId: location.state.projectId,
          surveyId: location.state.surveyId,
          audienceGroupId: location.state.audienceGroupId,
          surveyName: location.state.surveyName,
          createdAt: location.state.createdAt,
        };
        setProjectData(newProjectData);
        localStorage.setItem("currentProject", JSON.stringify(newProjectData));
        if (newProjectData.createdAt) {
          setIsLoadingProject(false);
        }
        return;
      }

      const storedData = localStorage.getItem("currentProject");
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          if (parsedData?.createdAt) {
            setProjectData(parsedData);
            setIsLoadingProject(false);
            return;
          }
        } catch (e) {
          console.error("Failed to parse stored project:", e);
        }
      }
      const fetchWithRetry = async (attempt = 1, maxAttempts = 5) => {
        try {
          const token = Cookies.get("token");
          const user_id = getUserId();

          if (!token || !user_id) {
            console.error("Missing token or user_id");
            setIsLoadingProject(false);
            return;
          }
          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/project/listAllSurveyProject`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                token: token,
              },
              body: JSON.stringify({
                user_id: user_id,
              }),
            },
          );

          const data = await response.json();

          if (data?.data && Array.isArray(data.data) && data.data.length > 0) {
            const latestProject = data.data.sort(
              (a, b) => new Date(b.created_at) - new Date(a.created_at),
            )[0];

            const fetchedProjectData = {
              projectId: latestProject._id,
              surveyId: latestProject.survey_id,
              audienceGroupId: latestProject.audience_grp_id,
              surveyName:
                latestProject.project_name || latestProject.survey_name,
              createdAt: latestProject.created_at,
            };

            setProjectData(fetchedProjectData);
            localStorage.setItem(
              "currentProject",
              JSON.stringify(fetchedProjectData),
            );
            setIsLoadingProject(false);
          } else {
            if (attempt < maxAttempts) {
              setTimeout(() => fetchWithRetry(attempt + 1, maxAttempts), 2000);
            } else {
              setIsLoadingProject(false);
            }
          }
        } catch (error) {
          if (attempt < maxAttempts) {
            setTimeout(() => fetchWithRetry(attempt + 1, maxAttempts), 2000);
          } else {
            setIsLoadingProject(false);
          }
        }
      };
      fetchWithRetry();
    };

    initializeProject();
  }, [location.state, getUserId]);

  useEffect(() => {
    if (!projectData?.createdAt) return;

    const createdTime = new Date(projectData.createdAt);
    const completionTime = new Date(
      createdTime.getTime() + PROJECT_COMPLETION_DURATION_MS,
    );
    const now = new Date();

    if (now >= completionTime) {
      setIsComplete(true);
      setCurrentStep(steps.length - 1);
      setProgress(100);
    }

    const timeString = completionTime.toLocaleString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });

    setDeliveryTime(timeString);
  }, [projectData, steps.length]);

  useEffect(() => {
    const clearSurveyFromIndexedDB = () => {
      const request = indexedDB.open("SurveyDB");
      request.onsuccess = (e) => {
        const db = e.target.result;
        if (db.objectStoreNames.contains("surveys")) {
          const tx = db.transaction("surveys", "readwrite");
          tx.objectStore("surveys").clear();
        }
      };
    };
    if (!projectData?.createdAt || isComplete) {
      if (isComplete) {
        const timer = setTimeout(() => {
          localStorage.removeItem("currentProject");
          localStorage.removeItem("pendingInviteData");
          localStorage.removeItem("selectedPlan");
          localStorage.removeItem("surveyMeta");
          localStorage.removeItem("linkedin_signup_flow");
          localStorage.removeItem("creatorFlowDone");
          localStorage.removeItem("userSession");
          clearSurveyFromIndexedDB();
          navigate("/projects");
        }, 2000);
        return () => clearTimeout(timer);
      }
      return;
    }

    const createdTime = new Date(projectData.createdAt);
    const totalDuration = PROJECT_COMPLETION_DURATION_MS;

    const animate = () => {
      const now = Date.now();
      const elapsed = now - createdTime.getTime();
      const totalProgress = Math.min((elapsed / totalDuration) * 100, 100);

      let cumulativePercentage = 0;
      let currentStepIndex = 0;
      let stepProgress = 0;

      for (let i = 0; i < steps.length; i++) {
        const stepEnd = cumulativePercentage + steps[i].percentage;

        if (totalProgress <= stepEnd) {
          currentStepIndex = i;
          stepProgress =
            ((totalProgress - cumulativePercentage) / steps[i].percentage) *
            100;
          stepProgress = Math.min(Math.max(stepProgress, 0), 100);
          break;
        }
        cumulativePercentage += steps[i].percentage;
      }

      if (
        currentStepIndex !== currentStep ||
        Math.abs(stepProgress - progress) > 0.1
      ) {
        setCurrentStep(currentStepIndex);
        setProgress(stepProgress);
      }

      if (totalProgress >= 100) {
        setCurrentStep(steps.length - 1);
        setProgress(100);
        setIsComplete(true);
        setTimeout(() => {
          localStorage.removeItem("currentProject");
          localStorage.removeItem("pendingInviteData");
          localStorage.removeItem("selectedPlan");
          localStorage.removeItem("surveyMeta");
          localStorage.removeItem("linkedin_signup_flow");
          localStorage.removeItem("creatorFlowDone");
          localStorage.removeItem("userSession");
          clearSurveyFromIndexedDB();
          navigate("/projects");
        }, 2000);
      }
    };

    animate();
    const interval = setInterval(animate, 2000);

    return () => {
      clearInterval(interval);
    };
  }, [projectData, isComplete, currentStep, progress, navigate, steps]);

  const getStepStatus = (stepIndex) => {
    if (stepIndex < currentStep) return "completed";
    if (stepIndex === currentStep) return "in-progress";
    return "pending";
  };

  const getStepProgress = (stepIndex) => {
    if (stepIndex < currentStep) return 100;
    if (stepIndex === currentStep) return progress;
    return 0;
  };
  const getTimeRemaining = () => {
    if (!projectData?.createdAt) return null;

    const createdTime = new Date(projectData.createdAt);
    const completionTime = new Date(
      createdTime.getTime() + PROJECT_COMPLETION_DURATION_MS,
    );
    const now = new Date();
    const remaining = completionTime - now;

    if (remaining <= 0) return "Complete!";

    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `~${hours}h ${minutes}m remaining`;
    }
    return `~${minutes}m remaining`;
  };

  return (
    <div className="page-bg">
      <div className="create-survey-logo">
        <Link to="/">
          <img
            src="/json-media/img/logo.svg"
            alt="GI"
            width={50}
            style={{ padding: 5 }}
          />
        </Link>
      </div>

      <div className="content-center">
        <div className="gi-container">
          <div style={{ position: "absolute", top: "2rem", right: "2rem" }}>
            <Link to="/projects">
              <button className="navbar-btn-secondary">
                Back to Projects →
              </button>
            </Link>
          </div>
          <div className="gi-header">
            <h1 className="gi-title">Generating Insights</h1>
            {isLoadingProject ? (
              <>
                <p className="gi-subtitle">
                  All set. You can close this page. We will send you an email
                  when your research is ready.
                </p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "0.75rem",
                    marginTop: "1.5rem",
                  }}
                >
                  <CircularProgress size={20} sx={{ color: "#3b82f6" }} />
                  <span style={{ color: "#3b82f6", fontSize: "0.95rem" }}>
                    Estimating time of delivery...
                  </span>
                </div>
              </>
            ) : (
              <>
                <p className="gi-subtitle">
                  All set. You can close this page. We will send you an email
                  when your research is ready.
                </p>
                <p className="gi-delivery">
                  Estimated time of delivery:{" "}
                  <span className="gi-delivery-time">{deliveryTime}</span>
                </p>
                {projectData?.createdAt && (
                  <p
                    style={{
                      color: "#3b82f6",
                      fontSize: "0.85rem",
                      marginTop: "0.5rem",
                      fontWeight: "500",
                    }}
                  >
                    {getTimeRemaining()}
                  </p>
                )}
              </>
            )}
          </div>
          {!isLoadingProject && projectData && (
            <div className="gi-progress-container">
              {steps.map((step, index) => (
                <div key={step.id} className="gi-step">
                  <div className="gi-step-indicator">
                    <div
                      className={`gi-step-circle gi-step-circle--${getStepStatus(index)}`}
                    >
                      {getStepStatus(index) === "completed" ? (
                        <svg
                          className="gi-check-icon"
                          viewBox="0 0 24 24"
                          fill="none"
                        >
                          <path
                            d="M9 12l2 2 4-4"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ) : getStepStatus(index) === "in-progress" ? (
                        <div className="gi-progress-number">{step.id}</div>
                      ) : (
                        <div className="gi-step-number">{step.id}</div>
                      )}
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={`gi-step-connector gi-step-connector--${
                          getStepStatus(index) === "completed"
                            ? "completed"
                            : "pending"
                        }`}
                      />
                    )}
                  </div>
                  <div className="gi-step-content">
                    <h3 className="gi-step-title">{step.title}</h3>
                    <p className="gi-step-description">{step.description}</p>
                    <div className="gi-step-status">
                      {getStepStatus(index) === "completed" ? (
                        <span className="gi-status-completed">Completed</span>
                      ) : getStepStatus(index) === "in-progress" ? (
                        <span className="gi-status-progress">
                          ({Math.round(getStepProgress(index))}%)
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {isLoadingProject && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: "300px",
              }}
            >
              <CircularProgress size={60} sx={{ color: "#3b82f6" }} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GeneratingInsights;
